import React, { useState } from 'react';

/**
 * Format currency values for display
 */
const formatCurrency = (value) => {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(value);
};

/**
 * Get style for allocation bar (handles special striped items)
 */
const getBarStyle = (itemName, weight, baseColor) => {
    const baseStyle = { width: `${weight}%` };

    if (itemName === 'Other Holdings') {
        return {
            ...baseStyle,
            background: `repeating-linear-gradient(
                45deg,
                #dc3545,
                #dc3545 4px,
                #212529 4px,
                #212529 8px
            )`
        };
    } else if (itemName.startsWith('RESTRICTED ')) {
        return {
            ...baseStyle,
            backgroundColor: '#dc3545',
            borderRight: '1px solid #b02a37'
        };
    } else if (itemName === 'Excess Cash') {
        return {
            ...baseStyle,
            background: `repeating-linear-gradient(
                45deg,
                #198754,
                #198754 4px,
                #0d5c36 4px,
                #0d5c36 8px
            )`
        };
    }

    return { ...baseStyle, backgroundColor: baseColor };
};

/**
 * Get style for legend swatch (handles special striped items)
 */
const getLegendStyle = (itemName, baseColor) => {
    const baseStyle = { width: '10px', height: '10px', display: 'inline-block' };

    if (itemName === 'Other Holdings') {
        return {
            ...baseStyle,
            background: `repeating-linear-gradient(
                45deg,
                #dc3545,
                #dc3545 2px,
                #212529 2px,
                #212529 4px
            )`
        };
    } else if (itemName.startsWith('RESTRICTED ')) {
        return {
            ...baseStyle,
            backgroundColor: '#dc3545'
        };
    } else if (itemName === 'Excess Cash') {
        return {
            ...baseStyle,
            background: `repeating-linear-gradient(
                45deg,
                #198754,
                #198754 2px,
                #0d5c36 2px,
                #0d5c36 4px
            )`
        };
    }

    return { ...baseStyle, backgroundColor: baseColor };
};

/**
 * AllocationVisualization - Displays Before/After/Target allocation bars
 * with a Sleeve/Security toggle.
 *
 * Props:
 *   sleeveAllocations    - Filtered sleeve allocation data
 *   securityAllocations  - Filtered security allocation data
 *   colorMap             - Color mapping for allocation names
 *   viewType             - (optional) Controlled view type: 'sleeve' | 'security'
 *   onViewTypeChange     - (optional) Callback when toggle is clicked: (type) => void
 */
const AllocationVisualization = React.memo(({ sleeveAllocations = [], securityAllocations = [], colorMap = {}, viewType: externalViewType, onViewTypeChange }) => {
    const [internalViewType, setInternalViewType] = useState('sleeve');
    const viewType = externalViewType !== undefined ? externalViewType : internalViewType;
    const setViewType = onViewTypeChange !== undefined ? onViewTypeChange : setInternalViewType;

    const allocations = viewType === 'sleeve' ? sleeveAllocations : securityAllocations;

    return (
        <div key={`allocation-viz-${viewType}`} className="mb-3 border rounded">
            <div className="card-header bg-light py-2 px-3 d-flex justify-content-between align-items-center">
                <h6 className="mb-0">
                    <i className={`bi ${viewType === 'sleeve' ? 'bi-collection' : 'bi-pie-chart'} me-2`}></i>
                    Group Allocation - By {viewType === 'sleeve' ? 'Sleeve' : 'Security'} ({allocations.length})
                </h6>
                <div className="btn-group btn-group-sm me-1" role="group">
                    <button
                        type="button"
                        className={`btn ${viewType === 'sleeve' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={(e) => { e.stopPropagation(); setViewType('sleeve'); }}
                        disabled={sleeveAllocations.length === 0}
                    >
                        <i className="bi bi-collection me-1"></i>Sleeve
                    </button>
                    <button
                        type="button"
                        className={`btn ${viewType === 'security' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={(e) => { e.stopPropagation(); setViewType('security'); }}
                        disabled={securityAllocations.length === 0}
                    >
                        <i className="bi bi-pie-chart me-1"></i>Security
                    </button>
                </div>
            </div>
            <div className="p-3">
                {/* Before */}
                <div className="mb-2 d-flex align-items-center">
                    <small className="text-muted fw-bold me-2" style={{ width: '45px', flexShrink: 0 }}>Before</small>
                    <div className="progress flex-grow-1" style={{ height: '20px' }}>
                        {allocations.map((item, idx) => {
                            const weight = item.beforeWeight || item.before_weight || 0;
                            return weight > 0 ? (
                                <div
                                    key={`before-${viewType}-${idx}`}
                                    className="progress-bar"
                                    style={getBarStyle(item.name, weight, colorMap[item.name])}
                                    title={`${item.name}: ${weight.toFixed(1)}% (${formatCurrency(item.beforeValue || item.before_value || 0)})`}
                                >
                                    {weight > 8 ? `${weight.toFixed(1)}%` : ''}
                                </div>
                            ) : null;
                        })}
                    </div>
                </div>
                {/* After */}
                <div className="mb-2 d-flex align-items-center">
                    <small className="text-muted fw-bold me-2" style={{ width: '45px', flexShrink: 0 }}>After</small>
                    <div className="progress flex-grow-1" style={{ height: '20px' }}>
                        {allocations.map((item, idx) => {
                            const weight = item.afterWeight || item.after_weight || 0;
                            return weight > 0 ? (
                                <div
                                    key={`after-${viewType}-${idx}`}
                                    className="progress-bar"
                                    style={getBarStyle(item.name, weight, colorMap[item.name])}
                                    title={`${item.name}: ${weight.toFixed(1)}% (${formatCurrency(item.afterValue || item.after_value || 0)})`}
                                >
                                    {weight > 8 ? `${weight.toFixed(1)}%` : ''}
                                </div>
                            ) : null;
                        })}
                    </div>
                </div>
                {/* Target */}
                <div className="mb-2 d-flex align-items-center">
                    <small className="text-muted fw-bold me-2" style={{ width: '45px', flexShrink: 0 }}>Target</small>
                    <div className="progress flex-grow-1" style={{ height: '20px' }}>
                        {allocations.map((item, idx) => {
                            const weight = item.targetWeight || item.target_weight || 0;
                            return weight > 0 ? (
                                <div
                                    key={`target-${viewType}-${idx}`}
                                    className="progress-bar"
                                    style={getBarStyle(item.name, weight, colorMap[item.name])}
                                    title={`${item.name}: ${weight.toFixed(1)}% (${formatCurrency(item.targetValue || item.target_value || 0)})`}
                                >
                                    {weight > 8 ? `${weight.toFixed(1)}%` : ''}
                                </div>
                            ) : null;
                        })}
                    </div>
                </div>
                {/* Legend */}
                <div className="d-flex flex-wrap mt-2" style={{ fontSize: '0.75rem' }}>
                    {allocations.map((item, idx) => {
                        const tradeImpact = item.tradeImpact || item.trade_impact || 0;
                        const beforeVal = item.beforeValue || item.before_value || 0;
                        const afterVal = item.afterValue || item.after_value || 0;
                        const valueChange = tradeImpact !== 0 ? tradeImpact : (afterVal - beforeVal);

                        const beforeWeight = item.beforeWeight || item.before_weight || 0;
                        const afterWeight = item.afterWeight || item.after_weight || 0;
                        const weightChange = afterWeight - beforeWeight;

                        return (
                            <div key={`legend-${viewType}-${idx}`} className="d-flex align-items-center me-3 mb-1">
                                <span className="me-1" style={getLegendStyle(item.name, colorMap[item.name])}></span>
                                <span>{item.name}</span>
                                {valueChange > 0.01 && (
                                    <span className="text-success ms-1" title={`Bought: ${formatCurrency(valueChange)}`}>
                                        <i className="bi bi-arrow-up-short"></i>
                                        <span style={{ fontSize: '0.7rem' }}>{weightChange.toFixed(1)}%</span>
                                    </span>
                                )}
                                {valueChange < -0.01 && (
                                    <span className="text-danger ms-1" title={`Sold: ${formatCurrency(Math.abs(valueChange))}`}>
                                        <i className="bi bi-arrow-down-short"></i>
                                        <span style={{ fontSize: '0.7rem' }}>{Math.abs(weightChange).toFixed(1)}%</span>
                                    </span>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
});

export default AllocationVisualization;
